import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { IMAGES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const label = "text-xs tracking-[0.2em] uppercase";
const field = "w-full bg-transparent py-2.5 outline-none text-base";
const line = { borderBottom: "1px solid var(--border-light)", color: "var(--text-primary)" };

export default function LeaveReview() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading");
  const [form, setForm] = useState({ name: "", role: "", quote: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/review-requests/${id}`, { params: { token } })
      .then(({ data }) => {
        setForm((f) => ({ ...f, name: data.name }));
        setState(data.submitted ? "done" : "form");
      })
      .catch(() => setState("error"));
  }, [id, token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.quote.trim().length < 10) {
      toast.error("Please add your name and a few words about your experience.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/review-requests/${id}/submit`, { token, ...form, role: form.role || "Client" });
      setState("done");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send your review. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen font-body px-6 py-14" style={{ backgroundColor: "var(--bg-secondary)" }}>
      <div data-testid="review-page" className="mx-auto max-w-xl rounded-3xl p-8 md:p-12 shadow-xl"
        style={{ backgroundColor: "var(--bg-primary)", border: "1px solid var(--border-light)" }}>
        <img src={IMAGES.logo} alt="ProExec Virtual Solutions" className="h-14 w-auto mx-auto mb-6" />
        {state === "loading" && <Loader2 className="animate-spin mx-auto" color="var(--rose-mocha)" />}
        {state === "error" && (
          <p data-testid="review-error" className="text-center" style={{ color: "var(--text-secondary)" }}>
            We couldn't find this review link. Please use the link from your email.
          </p>
        )}
        {state === "done" && (
          <div data-testid="review-thanks" className="text-center flex flex-col items-center gap-3">
            <CheckCircle2 size={36} color="var(--mocha-brown)" />
            <h1 className="font-serif text-3xl" style={{ color: "var(--text-primary)" }}>Thank you!</h1>
            <p style={{ color: "var(--text-secondary)" }}>Your kind words mean so much to us.</p>
          </div>
        )}
        {state === "form" && (
          <form onSubmit={submit} data-testid="review-form" className="flex flex-col gap-6">
            <div className="text-center">
              <h1 className="font-serif text-3xl md:text-4xl" style={{ color: "var(--text-primary)" }}>Share your experience</h1>
              <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
                A few words about working with ProExec Virtual Solutions.
              </p>
            </div>
            <div>
              <label className={label} style={{ color: "var(--rose-mocha-deep)" }}>Your name *</label>
              <input data-testid="review-name-input" value={form.name} className={field} style={line}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className={label} style={{ color: "var(--rose-mocha-deep)" }}>Title or company</label>
              <input data-testid="review-role-input" value={form.role} placeholder="e.g. CEO, Pratt Consulting"
                className={`${field} placeholder:opacity-40`} style={line}
                onChange={(e) => setForm({ ...form, role: e.target.value })} />
            </div>
            <div>
              <label className={label} style={{ color: "var(--rose-mocha-deep)" }}>Your review *</label>
              <textarea data-testid="review-quote-input" rows={5} maxLength={1200} value={form.quote}
                className={`${field} resize-none`} style={line}
                onChange={(e) => setForm({ ...form, quote: e.target.value })} />
            </div>
            <button type="submit" data-testid="review-submit-button" disabled={busy}
              className="self-start rounded-full px-8 py-3.5 text-sm font-medium disabled:opacity-60"
              style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}>
              {busy ? "Sending…" : "Submit review"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
